import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, abrirDocumento, type Documento, type Pedido } from "../api";
import EnviarDocumento from "./EnviarDocumento";
import StatusPedido from "./StatusPedido";

/**
 * MEUS DOCUMENTOS — tudo o que a empresa guarda da pessoa, por pasta.
 *
 * Junta a pasta do RH/DP (admissão, ASO, contratos…), os certificados da
 * Gestão da Operação e as folhas de ponto fechadas. A pessoa não precisa saber
 * em qual sistema cada papel mora — só que ele é dela e está aqui.
 *
 * Documento que está faltando pode ser ENVIADO daqui — mas vai para o DP
 * conferir antes de entrar na pasta oficial. Os enviados aparecem no topo com
 * a situação (aguardando, aprovado, recusado com o motivo).
 */

function tamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

function quando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR");
}

function norm(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/** Rótulo curto do tipo, no lugar de um ícone por extensão. */
function tipo(ext: string): string {
  const e = ext.toLowerCase();
  if (e === "pdf") return "PDF";
  if (["jpg", "jpeg", "png"].includes(e)) return "IMG";
  if (["doc", "docx"].includes(e)) return "DOC";
  if (["xls", "xlsx"].includes(e)) return "XLS";
  return (e || "ARQ").slice(0, 3).toUpperCase();
}

export default function Documentos() {
  const [docs, setDocs] = useState<Documento[] | null>(null);
  const [pastas, setPastas] = useState<string[]>([]);
  const [pasta, setPasta] = useState("");
  const [busca, setBusca] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviados, setEnviados] = useState<Pedido[]>([]);
  const [pastasEnvio, setPastasEnvio] = useState<string[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const [r, p] = await Promise.all([api.documentos(), api.pedidos()]);
      setDocs(r.documentos);
      setPastas(r.pastas);
      // Aprovados somem da lista de enviados: já estão na pasta, logo abaixo.
      setEnviados(p.solicitacoes.filter((x) => x.tipo === "documento" && x.status !== "Aprovada"));
      setPastasEnvio(p.pastas);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar os documentos.");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const visiveis = useMemo(() => {
    const q = norm(busca.trim());
    return (docs ?? [])
      .filter((d) => (!pasta || d.pasta === pasta) && (!q || norm(d.nome).includes(q)))
      .sort((a, b) => a.pasta.localeCompare(b.pasta, "pt-BR") || b.enviado_em.localeCompare(a.enviado_em));
  }, [docs, pasta, busca]);

  const porPasta = useMemo(() => {
    const mapa = new Map<string, Documento[]>();
    for (const d of visiveis) mapa.set(d.pasta, [...(mapa.get(d.pasta) ?? []), d]);
    return [...mapa.entries()];
  }, [visiveis]);

  async function abrir(d: Documento) {
    setAbrindo(d.caminho);
    setErro(null);
    try {
      await abrirDocumento(d.caminho, d.nome, d.ext);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível abrir o documento.");
    } finally {
      setAbrindo(null);
    }
  }

  if (!docs) {
    return erro ? <div className="aviso-erro">{erro}</div> : <div className="carregando">Carregando…</div>;
  }

  if (enviando) {
    return (
      <EnviarDocumento
        pastas={pastasEnvio}
        onCancelar={() => setEnviando(false)}
        onEnviado={(m) => {
          setEnviando(false);
          setAviso(m);
          carregar();
        }}
      />
    );
  }

  const topo = (
    <>
      {aviso && <div className="aviso-ok">{aviso}</div>}
      <button className="botao-principal" onClick={() => { setAviso(null); setEnviando(true); }}>
        Enviar documento que está faltando
      </button>
      {enviados.length > 0 && (
        <section className="grupo">
          <header className="grupo-topo">
            <span className="grupo-titulo">Enviados por você</span>
            <span className="grupo-total">{enviados.length}</span>
          </header>
          {enviados.map((e) => (
            <StatusPedido
              key={e.id}
              pedido={e}
              descricao={`${e.arquivo_nome ?? "Documento"}${e.dados?.pasta ? ` · ${e.dados.pasta}` : ""}`}
              onMudou={carregar}
            />
          ))}
        </section>
      )}
    </>
  );

  if (docs.length === 0) {
    return (
      <div className="pagina">
        {topo}
        <div className="vazio">
          Nenhum documento seu guardado ainda. Contratos, ASO, certificados e folhas de ponto aparecem
          aqui assim que o RH/DP os anexar.
        </div>
      </div>
    );
  }

  return (
    <div className="pagina">
      {topo}
      <div className="linha-filtro">
        <label className="campo campo-inline campo-cresce">
          <span>Buscar</span>
          <input
            type="search"
            placeholder="Nome do documento"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </label>
        <label className="campo campo-inline">
          <span>Pasta</span>
          <select value={pasta} onChange={(e) => setPasta(e.target.value)}>
            <option value="">Todas ({docs.length})</option>
            {pastas.map((p) => (
              <option key={p} value={p}>
                {p} ({docs.filter((d) => d.pasta === p).length})
              </option>
            ))}
          </select>
        </label>
      </div>

      {erro && <div className="aviso-erro">{erro}</div>}

      {visiveis.length === 0 ? (
        <div className="vazio">Nenhum documento encontrado com esse filtro.</div>
      ) : (
        porPasta.map(([nomePasta, itens]) => (
          <section key={nomePasta} className="grupo">
            <header className="grupo-topo">
              <span className="grupo-titulo">{nomePasta}</span>
              <span className="grupo-total">{itens.length}</span>
            </header>
            <ul className="lista">
              {itens.map((d) => (
                <li key={d.caminho}>
                  <button
                    className="item item-clicavel"
                    onClick={() => abrir(d)}
                    disabled={abrindo === d.caminho}
                    title="Abrir"
                  >
                    <span className="doc-tipo">{tipo(d.ext)}</span>
                    <span className="doc-texto">
                      <span className="doc-nome">{d.nome}</span>
                      <span className="item-detalhe">
                        {abrindo === d.caminho
                          ? "Abrindo…"
                          : [quando(d.enviado_em), tamanho(d.tamanho), d.origem !== "RH/DP" ? d.origem : ""]
                              .filter(Boolean)
                              .join(" · ")}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
